// src/main/content/contests/ContestList.js
//
// Card grid of contests. Filtering is entirely the parent's concern
// (ContestsDisplay owns the status tab + filter dropdowns + search). This
// component just renders whatever it's handed.
//
// Card surfaces the real data the backend returns: name, description, type
// chip, store names, status badge (derived from start/end dates, refreshed
// on every read), date range with progress bar, metric label + format, and
// Top N winners. Admins/managers see an edit affordance.
//
// Empty state is fully driven by the ``emptyState`` prop the parent passes
// in. The parent knows which status tab is active and whether dropdown
// filters are restrictive, so it can build a tailored message + decide
// whether the Create CTA fits (e.g., it doesn't on the Completed tab —
// you can't create a contest that's already over).

import React from "react";
import dayjs from "dayjs";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import BoltIcon from "@mui/icons-material/Bolt";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { formatDateReadable } from "../requests/utilities/helpers";
import { useIsTruncated } from "./useIsTruncated";

// Small, monochrome "Individual" / "Store vs Store" pill. Lives in the
// meta row below the title (not the top-right corner) — the top-right is
// reserved for the status pill, which earns its prominence by being the
// thing viewers scan for first.
function TypeChip({ contestType }) {
  const isStore = contestType === "store_vs_store";
  const label = isStore ? "Store vs Store" : "Individual";
  return (
    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600 shrink-0">
      {label}
    </span>
  );
}

// Comma-joined store list with a "+N more" tail when the list exceeds
// ``maxVisible``. Returns both the visible text and a ``truncated`` flag so
// callers can decide whether a "full list on hover" tooltip is useful — if
// every name already fits, the tooltip would just echo the visible text.
function formatStoreNamesShort(storeNames, maxVisible = 3) {
  if (!Array.isArray(storeNames) || storeNames.length === 0) {
    return { text: "", truncated: false };
  }
  const visible = storeNames.slice(0, maxVisible);
  const remaining = storeNames.length - visible.length;
  return {
    text:
      remaining > 0
        ? `${visible.join(" · ")} · +${remaining} more`
        : visible.join(" · "),
    truncated: remaining > 0,
  };
}

// Style maps live at module scope so both the status pill and the progress
// bar can use them — keeps the pill color and the progress fill in sync per
// status. Active is violet (matches the brand "look here, it's happening
// now" tone), Completed is emerald (positive but settled), Upcoming is
// slate (neutral, not started).
const STATUS_PILL_STYLE = {
  Active: "bg-violet-50 text-violet-700 border-violet-200",
  Upcoming: "bg-slate-50 text-slate-600 border-slate-200",
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const STATUS_PROGRESS_FILL = {
  Active: "bg-violet-500",
  Upcoming: "bg-slate-300",
  Completed: "bg-emerald-500",
};

const STATUS_ICON = {
  Active: BoltIcon,
  Upcoming: ScheduleIcon,
  Completed: CheckCircleIcon,
};

const STATUS_LABEL = {
  Active: "Live",
  Upcoming: "Upcoming",
  Completed: "Completed",
};

// Ordinal suffix for 1st / 2nd / 3rd / 4th / 21st / etc. Standard English
// rule: ``11``, ``12``, ``13`` are exceptions ("th"); everything else
// derives from the last digit. We only render this for winners (rank <=
// winners_count), so the higher-rank values stay rare.
function ordinal(n) {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

// "Rank X of Y" pill — surfaces the viewer's standing so the contests
// page reads as a personal scoreboard. Three tiers:
//   * 1st-place winner: amber + trophy (celebrate the leader).
//   * Other winners (rank <= winners_count): emerald + trophy
//     ("you're in the prize zone").
//   * Everyone else: slate ("here's where you are, neutral").
// For store-vs-store contests we prefix with the user's best-ranked
// store so a user with multiple stores in the contest knows which one
// the pill is reporting on.
function ViewerRankPill({ position }) {
  if (!position) return null;
  const { rank, total, is_winner, subject_label } = position;
  if (!rank || !total) return null;
  const isFirst = rank === 1;
  const showTrophy = is_winner;
  const color = isFirst
    ? "bg-amber-50 text-amber-700 border-amber-200"
    : is_winner
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-slate-50 text-slate-600 border-slate-200";
  // Winners read more celebratory in ordinal form ("1st of 12") than
  // "Rank 1 of 12"; everyone else stays in "Rank X" framing.
  const rankText = is_winner ? `${ordinal(rank)} of ${total}` : `Rank ${rank} of ${total}`;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium shrink-0 ${color}`}
    >
      {showTrophy && (
        <EmojiEventsIcon style={{ fontSize: 12 }} />
      )}
      {subject_label ? `${subject_label} · ${rankText}` : rankText}
    </span>
  );
}

// Amber to stand apart from all three status pill colors — a draft's
// date-derived status is secondary to the fact it isn't published yet.
// True when the contest awards every subject that hits its own target
// (per-store-target metrics only) instead of a fixed top-N.
export function isHitTargetContest(contest) {
  return contest?.winner_mode === "hit_target";
}

// Short "Winners" stat label shared by the list card and detail header.
export function winnersLabel(contest) {
  return isHitTargetContest(contest)
    ? "Hit target"
    : `Top ${contest.winners_count}`;
}

export function DraftBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium shrink-0 bg-amber-50 text-amber-700 border-amber-200">
      <EditNoteIcon style={{ fontSize: 12 }} />
      Draft
    </span>
  );
}

function StatusBadge({ status }) {
  const color = STATUS_PILL_STYLE[status] || STATUS_PILL_STYLE.Upcoming;
  const Icon = STATUS_ICON[status] || ScheduleIcon;
  const label = STATUS_LABEL[status] || status;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium shrink-0 ${color}`}
    >
      <Icon style={{ fontSize: 12 }} />
      {label}
    </span>
  );
}

function ContestCard({ contest, isAdmin, onSelect, onEdit }) {
  // Millisecond-level progress so the bar doesn't snap to 100% at the
  // start of the final day while the contest is still Active. Matches the
  // hero progress bar's calc and the countdown's anchor — both treat the
  // contest as start_date.startOf("day") → end_date.endOf("day").
  const startMs = dayjs(contest.start_date).startOf("day").valueOf();
  const endMs = dayjs(contest.end_date).endOf("day").valueOf();
  const totalMs = Math.max(1, endMs - startMs);
  const elapsedMs = Math.max(0, Math.min(totalMs, Date.now() - startMs));
  const progressPct =
    contest.status === "Completed"
      ? 100
      : Math.round((elapsedMs / totalMs) * 100);

  const { text: storeNamesText, truncated: storeNamesTruncated } =
    formatStoreNamesShort(contest.store_names);
  // Two ways the visible store-names text can be incomplete:
  //   1. We dropped names off the end with a "+N more" tail
  //      (``storeNamesTruncated`` — count-side).
  //   2. The visible text is too wide for the meta row and is being
  //      ellipsized by ``truncate`` (layout-side). Common for 1-3
  //      contests with very long store names.
  // Either case warrants a tooltip showing the full comma-joined list.
  const [storeNamesRef, storeNamesLayoutTruncated] =
    useIsTruncated(storeNamesText);
  const showStoreNamesTooltip =
    !!storeNamesText && (storeNamesTruncated || storeNamesLayoutTruncated);
  const progressFill =
    STATUS_PROGRESS_FILL[contest.status] || STATUS_PROGRESS_FILL.Upcoming;

  return (
    <div
      onClick={() => onSelect(contest)}
      className="group relative cursor-pointer rounded-2xl border border-slate-200 bg-white p-5 hover:border-slate-300 hover:shadow-md transition-all duration-200 flex flex-col"
    >
      {/* Title row — status pill alone on the right so it stays prominent
          and doesn't get squished by a stacked second chip. Drafts add the
          amber Draft pill in front of it. */}
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-slate-900 truncate flex-1 min-w-0">
          {contest.name}
        </h3>
        {contest.is_draft && <DraftBadge />}
        <StatusBadge status={contest.status} />
      </div>

      {/* Meta row — type chip + viewer rank pill + truncated store list.
          Keeps secondary metadata together one line below the title,
          freeing the top-right corner for the status pill. The viewer
          rank pill only renders when the backend supplied a position
          (Upcoming and unranked-by-this-viewer contests have it null),
          so the row gracefully reduces to type + stores for admins
          viewing contests they're not personally in. */}
      <div className="mt-1.5 flex items-center gap-2 min-w-0">
        <TypeChip contestType={contest.contest_type} />
        <ViewerRankPill position={contest.viewer_position} />
        {storeNamesText &&
          // Show the full-list tooltip in either a "+N more" case OR when
          // a 1-3 store row's text is layout-truncated by width. The hook
          // observes the actual DOM via ResizeObserver, so this stays
          // correct across viewport changes without us guessing widths.
          //
          // ``block`` is required for the ``truncate`` classes to actually
          // clip — Tailwind's ``truncate`` relies on ``overflow: hidden``
          // + ``text-overflow: ellipsis``, which only apply to block /
          // inline-block elements.
          (showStoreNamesTooltip ? (
            <Tooltip
              title={(contest.store_names || []).join(", ")}
              arrow
              placement="top"
            >
              <span
                ref={storeNamesRef}
                className="block text-xs text-slate-500 truncate min-w-0"
              >
                {storeNamesText}
              </span>
            </Tooltip>
          ) : (
            <span
              ref={storeNamesRef}
              className="block text-xs text-slate-500 truncate min-w-0"
            >
              {storeNamesText}
            </span>
          ))}
      </div>

      {/* Date range + progress. Placed BEFORE the description so its vertical
          position is the same on every card — description is the only
          variable-height block, and putting it last means the dates and
          progress bar always land at the same y-coordinate across the row. */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
          <span>{formatDateReadable(contest.start_date)}</span>
          <span>{formatDateReadable(contest.end_date)}</span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div
            className={`h-full transition-[width] duration-500 ${progressFill}`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Description — line-clamp-2 with a reserved min-height so cards that
          lack a description don't snap shorter and break row alignment. */}
      <p className="text-xs text-slate-500 mt-3 line-clamp-2 min-h-[2rem]">
        {contest.description || ""}
      </p>

      {/* Metric + winners */}
      <div className="mt-2 grid grid-cols-2 gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium mb-0.5">
            Metric
          </div>
          <div className="text-sm font-medium text-slate-900 truncate">
            {contest.metric_label || "—"}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium mb-0.5">
            Winners
          </div>
          <div className="text-sm font-medium text-slate-900 flex items-center gap-1">
            <EmojiEventsIcon className="text-amber-500" style={{ fontSize: 14 }} />
            {winnersLabel(contest)}
          </div>
        </div>
      </div>

      {/* Hover edit affordance — absolutely positioned, fades in via
          ``group-hover`` so the card stays clean at rest and the action
          reveals on intent. Same pattern the swipe deck cards use. The
          whole card is already clickable to open the detail view, so we
          don't need a separate chevron CTA — the cursor + hover shadow
          signal interactivity, and the edit button is the only thing
          that needs a distinct affordance. */}
      {isAdmin && (
        // ``pointer-events-none`` while hidden so the invisible IconButton
        // doesn't intercept clicks/taps in the bottom-right corner of the
        // card. Without it, ``opacity-0`` only hides the pixels — the
        // hitbox stays live, which is especially broken on touch devices
        // where there's no hover state to ever reveal the button.
        // ``group-hover`` restores both visibility AND pointer events at
        // the same time, keeping desktop hover behavior unchanged.
        <div className="absolute bottom-3 right-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(contest);
            }}
            sx={{
              backgroundColor: "rgba(255,255,255,0.92)",
              border: "1px solid #e2e8f0",
              color: "#475569",
              // Brand-violet on direct hover so the action reads as a
              // primary CTA the moment the cursor lands on it.
              "&:hover": {
                backgroundColor: "#6d28d9",
                borderColor: "#6d28d9",
                color: "#ffffff",
              },
            }}
          >
            <EditIcon style={{ fontSize: 18 }} />
          </IconButton>
        </div>
      )}
    </div>
  );
}

export default function ContestList({
  contests,
  isAdmin,
  onEdit,
  onSelect,
  onCreate,
  emptyState = {
    title: "No contests yet",
    message: "",
    showCreate: false,
  },
}) {
  const items = contests || [];

  if (items.length === 0) {
    // Empty-state config is built by the parent so the message + CTA can
    // be status-aware (e.g., "Nothing scheduled yet" + Create on Upcoming
    // tab, but "No completed contests yet" without a Create on Completed).
    // ContestList stays presentational — it just renders what it's told.
    return (
      <div className="w-full px-10 pb-20">
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center mb-4">
            <EmojiEventsIcon
              className="text-slate-400"
              style={{ fontSize: 28 }}
            />
          </div>
          <h3 className="text-base font-semibold text-slate-900">
            {emptyState.title}
          </h3>
          {emptyState.message && (
            <p className="text-sm text-slate-500 mt-1 max-w-sm">
              {emptyState.message}
            </p>
          )}
          {emptyState.showCreate && isAdmin && onCreate && (
            <button
              onClick={onCreate}
              className="mt-5 px-4 py-2 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium flex items-center gap-1.5"
            >
              <AddIcon style={{ fontSize: 18 }} />
              New Contest
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-10 pb-20">
      <div className="grid grid-cols-3 lg:grid-cols-2 md:grid-cols-1 gap-4">
        {items.map((c) => (
          <ContestCard
            key={c.id}
            contest={c}
            isAdmin={isAdmin}
            onSelect={onSelect}
            onEdit={onEdit}
          />
        ))}
      </div>
    </div>
  );
}
